import { Component, h, Host, Prop, State, Watch } from '@stencil/core';
import { store } from '../../../redux/store';
import { isSameDay } from 'date-fns';
import { fetchDaysAvailability, fetchPossibleHours } from '../../../api';
import { dateToHourString, dateToString, priceWithMileagePerTime, wholeMonthDateRange } from '../../../utils/utils';
import { addItemToCart, clearLocationId, fetchPrice, openCheckout, openReservation, requireInit } from '../../../redux/actions';
import { BookingDetails, Cart, ItemDetails, PriceOptions, TimeSlotPicker, Translations, Widget } from '../../../redux/types';
import { trans } from '../../../utils/trans';
import { RentwareCheckoutTemplate } from '../../rentware-checkout/rentware-checkout-modal/template/rentware-checkout-template';
import { ComponentView, DaysAvailability, DayState, Price } from '../../../types/types';

@Component({
  tag: 'rentware-article-booking-range-engine',
  shadow: false,
})
export class RentwareArticleBookingRangeEngine {
  @Prop() articleId: string;
  @Prop() itemDetails: ItemDetails;
  @Prop() selectedLocationId?: string;
  @Prop() selectedVariationId?: string;
  @Prop() selectedMileageId?: string;
  @Prop() widget: Widget;
  @Prop() timePicker: TimeSlotPicker;
  @Prop() locale: string;
  @Prop() cartState: Cart;
  @Prop() translations: Translations;
  @Prop() loadedComponents;
  @Prop() priceOptions: PriceOptions;
  @Prop() reservationPopup: ComponentView[];
  @Prop() bookingDetails: BookingDetails;

  @State() isLoading: boolean = false;
  @State() isCalendarLoading: boolean = false;
  @State() loadingPickUpHours: boolean = false;
  @State() loadingDropOffHours: boolean = false;
  @State() error: boolean = false;
  @State() pickUpDate: Date;
  @State() pickUpTime: string;
  @State() possibleHours: {} = {};
  @State() dropOffDate: Date;
  @State() suspectedDropOffDate: Date = null;
  @State() dropOffDateAsPickupDate: boolean = false;
  @State() dropOffTime: string;
  @State() price: Price = { available: false, gross: 0, net: 0 };
  @State() amountToBook: number = 1;
  @State() daysAvailability: DaysAvailability;
  @State() firstAvailableDate: Date;
  @State() lastAvailableDate: Date;
  @State() selectedMonth: Date = new Date();
  @State() timeSlotPicker: TimeSlotPicker;

  openCheckout!: typeof openCheckout;
  addItemToCart!: typeof addItemToCart;
  requireInit!: typeof requireInit;
  fetchPrice!: typeof fetchPrice;
  errorTimer: ReturnType<typeof setTimeout>;
  openReservation!: typeof openReservation;
  clearLocationId!: typeof clearLocationId;

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      addItemToCart,
      requireInit,
      openCheckout,
      fetchPrice,
      openReservation,
      clearLocationId,
    });

    if (this.timePicker) {
      this.timeSlotPicker = {
        ...this.timePicker,
        onOptionChange: this.handleTimePickerOptionChange,
      };
    }

    /***↓ Renteer ↓***/
    const url = new URL(document.location.toString());
    const { bookingDate } = Object.fromEntries(url.searchParams.entries());

    if (!!bookingDate) {
      this.doRenteerShieeet();
    } else {
      /***↑ Renteer ↑***/
      /*** After delete Renteer code leave fetchPickUpDays (below) without any condition ***/
      this.fetchPickUpDays();
    }
  }

  @Watch('orderDetailsState')
  watchOrderDetailsHandler(newValue) {
    //call if cart becomes empty
    if (!newValue || !newValue.id) {
      this.fetchPickUpDays();
      this.validateAndFetchPrice();
    }
  }

  disconnectedCallback() {
    clearInterval(this.errorTimer);
  }

  componentDidLoad() {
    this.requireInit();
  }

  componentWillRender() {
    const orderDetails = this.bookingDetails?.orderDetails;

    if (orderDetails) {
      const fromDateTime = new Date(orderDetails.fromDate);
      const toDateTime = new Date(orderDetails.toDate);

      if (!isSameDay(fromDateTime, this.pickUpDate)) {
        this.pickUpDate = new Date(fromDateTime);
      }
      if (!isSameDay(new Date(orderDetails.toDate), this.dropOffDate)) {
        this.dropOffDate = new Date(toDateTime);
      }
      if (dateToHourString(fromDateTime) !== this.pickUpTime) {
        this.pickUpTime = dateToHourString(fromDateTime);
      }
      if (dateToHourString(toDateTime) !== this.dropOffTime) {
        this.dropOffTime = dateToHourString(toDateTime);
      }
    }

    if (this.amountToBook < this.itemDetails?.minQuantity) {
      this.amountToBook = this.itemDetails.minQuantity;
    }
  }

  handleTimePickerOptionChange = value => {
    this.timeSlotPicker = { ...this.timeSlotPicker, selectedOption: value };
    this.validateAndFetchPrice();
  };

  hideError = () => {
    clearInterval(this.errorTimer);
    this.error = false;
  };

  showErrorForTime = (time: number = 10000) => {
    this.error = true;

    clearInterval(this.errorTimer);
    this.errorTimer = setTimeout(() => {
      this.error = false;
    }, time);
  };

  handleOpenCheckout = () => {
    this.openCheckout();

    const intervalId = setInterval(() => {
      const checkoutLoaded = !!this.loadedComponents[RentwareCheckoutTemplate.name];

      if (!checkoutLoaded) {
        return;
      }

      clearInterval(intervalId);
    }, 100);
  };

  handleAddToCart = () => {
    this.loadingStarted();
    const pickUpTime = this.withTime && !this.timeSlotPicker ? this.pickUpTime + ':00' : '12:00:00';
    const dropOffTime = this.withTime && this.dropOffTime && !this.timeSlotPicker ? this.dropOffTime + ':00' : '12:00:00';
    let variationsList = [];
    if (!!this.selectedVariationId) {
      variationsList = Array(this.amountToBook).fill(this.selectedVariationId);
    }

    this.addItemToCart(
      {
        itemId: this.articleId,
        fullDay: !this.withDateRange && !this.withTime,
        dateRange: {
          from: dateToString(this.pickUpDate) + 'T' + pickUpTime,
          to: dateToString(this.dropOffDate || this.pickUpDate) + 'T' + dropOffTime,
        },
        timeSlot: this.timeSlotPicker?.selectedOption,
        amountToAdd: this.amountToBook,
        variationsIds: variationsList,
        mileageOptionId: this.selectedMileageId,
        locationId: this.selectedLocationId,
        people: this.bookingDetails.peopleNumber,
        price: this.price,
        name: this.itemDetails.name,
      },
      () => {
        const showReservation = this.reservationPopup?.includes(ComponentView.Calendar);

        this.hideError();
        if (showReservation) {
          this.openReservation(this.itemDetails.id);
        }
        this.loadingFinished();
      },
      () => {
        this.loadingFinished();
        this.showErrorForTime(10000);
      },
    );
  };

  resetDateAndTime = () => {
    this.pickUpDate = null;
    this.pickUpTime = '';
    this.dropOffDate = null;
    this.dropOffTime = '';
    this.suspectedDropOffDate = null;
  };

  handleLocationChange = (id: string) => {
    this.selectedLocationId = id;
    this.clearLocationId(); // clear what's in the global state
    this.resetDateAndTime();

    this.validateAndFetchPrice(); // this calls only synchronous part of validateAndFetchPrice()
    this.fetchPickUpDays();
  };

  handleVariationChange = (id: string) => {
    this.selectedVariationId = id;
    this.resetDateAndTime();

    this.validateAndFetchPrice(); // this calls only synchronous part of validateAndFetchPrice()
    this.fetchPickUpDays();
  };

  handleMileageChange = (id: string) => {
    this.selectedMileageId = id;
    this.validateAndFetchPrice();
  };

  handleChangeAmountToBook = newAmount => {
    this.amountToBook = newAmount;
    this.validateAndFetchPrice(false);
  };

  validateAndFetchPrice(runWithLoading: boolean = true) {
    let runValidate = false;

    if (this.timeSlotPicker) {
      if (!!this.pickUpDate && this.timeSlotPicker.selectedOption) {
        runValidate = true;
      }
    } else {
      if (this.noDateNoTime || this.dateNoTime) {
        if (!!this.pickUpDate && (!!this.suspectedDropOffDate || this.dropOffDate)) {
          runValidate = true;
        }
      } else if (this.noDateTime || this.dateTime) {
        if (!!this.dropOffTime) {
          runValidate = true;
        }
      }
    }

    if (runValidate) {
      if (runWithLoading) {
        this.loadingStarted();
      }

      this.fetchPrice(
        this.articleId,
        this.selectedVariationId,
        {
          from: dateToString(this.pickUpDate),
          to: dateToString(this.dropOffDate || this.suspectedDropOffDate),
        },
        {
          from: this.pickUpTime,
          to: this.dropOffTime,
        },
        this.timeSlotPicker?.selectedOption,
        this.amountToBook,
        (data: any) => {
          this.price = {
            available: data.available,
            gross: data.price,
            net: data.netPrice,
            mileageOptions: data.mileageOptions,
            quantity: {
              min: data.minQuantity,
              max: data.maxQuantity,
              available: data.availableQuantity,
            },
          };

          this.priceOptions = { ...this.priceOptions, priceCalculation: data.priceCalculationType };
          this.loadingFinished();
        },
        this.loadingFinished,
      );
    } else {
      this.price = { available: false, gross: 0, net: 0, vat: 0, mileageOptions: [] };
    }
  }

  get dateTime() {
    return this.withDateRange && this.withTime;
  }

  get dateNoTime() {
    return this.withDateRange && !this.withTime;
  }

  get noDateTime() {
    return !this.withDateRange && this.withTime;
  }

  get noDateNoTime() {
    return !this.withDateRange && !this.withTime;
  }

  handleResetCalendar = (day?: Date) => {
    // reset only drop-off
    if (day && this.withDateRange && isSameDay(day, this.dropOffDate) && !isSameDay(this.pickUpDate, this.dropOffDate)) {
      this.dropOffDate = null;
      this.dropOffTime = null;
      this.suspectedDropOffDate = null;
      this.fetchDropOffDays();
    } else {
      // reset full calendar
      if (day) {
        this.selectedMonth = day;
      }

      this.resetDateAndTime();

      if (this.timeSlotPicker) {
        this.timeSlotPicker = { ...this.timeSlotPicker, selectedOption: null };
      }
      this.fetchPickUpDays();
    }
    this.validateAndFetchPrice();
  };

  handleDayClick = date => {
    if (this.noDateNoTime) {
      this.handleNoDateNoTimeClick(date);
    } else if (this.dateNoTime) {
      this.handleDateNoTimeClick(date);
    } else if (this.noDateTime) {
      this.handleNoDateTimeClick(date);
    } else if (this.dateTime) {
      this.handleDateTimeClick(date);
    }
  };

  handleNoDateNoTimeClick = date => {
    // only case: every click on day picks a new date
    this.pickUpDate = date;
    this.dropOffDate = date;
    this.validateAndFetchPrice();
  };

  handleNoDateTimeClick = date => {
    // only case: every click on day picks a new date
    this.pickUpDate = date;
    this.dropOffDate = date;
    this.pickUpTime = '';
    this.dropOffTime = '';
    if (this.timeSlotPicker) {
      this.timeSlotPicker = { ...this.timeSlotPicker, selectedOption: null };
    } else {
      this.fetchPickupHours();
    }
    this.validateAndFetchPrice();
  };

  handleDateNoTimeClick = date => {
    // case 1: empty calendar
    if (!this.pickUpDate) {
      this.pickUpDate = date;
      this.fetchDropOffDays();

      // case 2: select a drop-off or act as drop-off === pick-up
    } else if (!!this.pickUpDate && !this.dropOffDate) {
      this.dropOffDate = date;
      this.suspectedDropOffDate = null;
    }

    this.validateAndFetchPrice();
  };

  handleDateTimeClick = date => {
    // case 1: empty calendar
    if (!this.pickUpDate) {
      this.pickUpDate = date;
      this.fetchDropOffDays(this.fetchPickupHours);

      // case 2: select a drop-off or act as drop-off === pick-up
    } else if (!!this.pickUpDate && !this.dropOffDate) {
      this.dropOffDate = date;
      this.suspectedDropOffDate = null;
      this.fetchDropOffHours();
    }

    this.validateAndFetchPrice();
  };

  handlePickUpTimeChange = value => {
    this.pickUpTime = value;
    this.dropOffTime = null;

    const dropOffDate = !!this.dropOffDate ? dateToString(this.dropOffDate) : !!this.suspectedDropOffDate ? dateToString(this.suspectedDropOffDate) : null;

    fetchPossibleHours(
      this.articleId,
      this.selectedVariationId,
      dateToString(this.pickUpDate),
      dropOffDate,
      (hours: any) => {
        this.possibleHours = hours;
        this.loadingDropOffHours = false;

        // case when pickUp hour was not yet selected but from dropOff point of view there is only one pickUp
        // hour possible
        if (!this.pickUpTime) {
          if (this.pickUpHours.length === 1) {
            this.pickUpTime = this.pickUpHours[0];
          }
        }

        if (this.pickUpTime) {
          const dropOffTimeObjectLength: number = this.possibleHours[this.pickUpTime].length;

          if (dropOffTimeObjectLength === 1) {
            this.handleDropOffTimeChange(this.possibleHours[this.pickUpTime][0]);
          }
        }
      },
      () => {
        this.loadingDropOffHours = false;
      },
      this.selectedLocationId,
    );

    this.validateAndFetchPrice();
  };

  handleDropOffTimeChange = value => {
    this.dropOffTime = value;
    this.validateAndFetchPrice();
  };

  fetchPickupHours = () => {
    this.loadingPickUpStarted();
    this.pickUpTime = null;
    this.dropOffTime = null;

    const dropOffDate = !!this.dropOffDate ? dateToString(this.dropOffDate) : !!this.suspectedDropOffDate ? dateToString(this.suspectedDropOffDate) : null;

    fetchPossibleHours(
      this.articleId,
      this.selectedVariationId,
      dateToString(this.pickUpDate),
      dropOffDate,
      (hours: any) => {
        this.possibleHours = hours;
        const pickupTimeObjectLength: number = Object.keys(this.possibleHours).length;
        if (pickupTimeObjectLength === 1) {
          const pickupHour = Object.keys(this.possibleHours)[0];
          this.handlePickUpTimeChange(pickupHour);

          if (!this.withDateRange) {
            const dropOffTimeObjectLength: number = this.possibleHours[pickupHour].length;
            if (dropOffTimeObjectLength === 1) {
              this.handleDropOffTimeChange(this.possibleHours[pickupHour]);
            }
          }
        }

        this.loadingPickUpFinished();
      },
      this.loadingPickUpFinished,
      this.selectedLocationId,
    );
  };

  fetchDropOffHours = () => {
    this.loadingDropOffStarted();
    this.dropOffTime = null;

    fetchPossibleHours(
      this.articleId,
      this.selectedVariationId,
      dateToString(this.pickUpDate),
      dateToString(this.dropOffDate || this.suspectedDropOffDate),
      (hours: any) => {
        this.possibleHours = hours;
        this.loadingDropOffFinished();

        const dropOffTimeObjectLength: number = this.possibleHours[this.pickUpTime].length;
        if (dropOffTimeObjectLength === 1) {
          this.handleDropOffTimeChange(this.possibleHours[this.pickUpTime][0]);
        }
      },
      this.loadingDropOffFinished,
      this.selectedLocationId,
    );
  };

  handleChangeMonth = (date: Date) => {
    this.selectedMonth = date;
    const isPickUpMode: boolean = !this.withDateRange || !this.pickUpDate;

    if (isPickUpMode) {
      this.fetchPickUpDays();
    } else {
      this.fetchDropOffDays();
    }
  };

  fetchPickUpDays = () => {
    this.loadingCalendarStarted();
    const selectedMonth = wholeMonthDateRange(this.selectedMonth);

    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      selectedMonth,
      'pickUp',
      null,
      this.loadPickUpDays,
      this.loadingCalendarFinished,
      null,
      this.selectedLocationId,
    );
  };

  fetchDropOffDays = (onSuccess?) => {
    this.loadingCalendarStarted();
    const selectedMonth = wholeMonthDateRange(this.selectedMonth);

    fetchDaysAvailability(
      this.articleId,
      this.selectedVariationId,
      selectedMonth,
      'dropOff',
      this.pickUpDate ? dateToString(this.pickUpDate) : null,
      data => {
        this.loadDropOffDays(data);
        onSuccess?.();
      },
      this.loadingCalendarFinished,
      null,
      this.selectedLocationId,
    );
  };

  loadPickUpDays = data => {
    this.daysAvailability = data.days;
    this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
    this.lastAvailableDate = null;

    this.loadingCalendarFinished();
  };

  loadDropOffDays = data => {
    this.daysAvailability = data.days;
    this.firstAvailableDate = data.nearestAvailableDate ? new Date(data.nearestAvailableDate) : null;
    this.lastAvailableDate = data.lastAvailableDate ? new Date(data.lastAvailableDate) : null;

    const pickUpString = dateToString(this.pickUpDate);
    const pickUpAvailableAsDropOff = this.daysAvailability[pickUpString]?.state === DayState.Selectable;

    if (pickUpAvailableAsDropOff) {
      this.suspectedDropOffDate = this.pickUpDate;
      if (!this.withTime) {
        this.validateAndFetchPrice();
      }
    } else {
      this.suspectedDropOffDate = null;
    }

    this.loadingCalendarFinished();
  };

  loadingPickUpStarted = () => {
    this.loadingPickUpHours = true;
  };

  loadingPickUpFinished = () => {
    this.loadingPickUpHours = false;
  };

  loadingCalendarStarted = () => {
    this.isCalendarLoading = true;
  };

  loadingCalendarFinished = () => {
    this.isCalendarLoading = false;
  };

  loadingStarted = () => {
    this.isLoading = true;
  };

  loadingFinished = () => {
    this.isLoading = false;
  };

  loadingDropOffStarted = () => {
    this.loadingDropOffHours = true;
  };

  loadingDropOffFinished = () => {
    this.loadingDropOffHours = false;
  };

  doRenteerShieeet = () => {
    const url = new URL(document.location.toString());
    const { bookingDate, endDate, bookingTime, endTime } = Object.fromEntries(url.searchParams.entries());

    this.pickUpDate = new Date(bookingDate);
    this.dropOffDate = new Date(endDate);
    this.pickUpTime = bookingTime;
    this.dropOffTime = endTime;

    this.handleChangeMonth(new Date(endDate));

    if (this.withTime) {
      this.loadingDropOffStarted();

      fetchPossibleHours(
        this.articleId,
        this.selectedVariationId,
        dateToString(this.pickUpDate),
        dateToString(this.dropOffDate || this.suspectedDropOffDate),
        (hours: any) => {
          this.possibleHours = hours;
          this.loadingDropOffFinished();
          this.validateAndFetchPrice();
        },
        this.loadingDropOffFinished,
        this.selectedLocationId,
      );
    } else {
      this.validateAndFetchPrice();
    }
  };

  get withTime(): boolean {
    return this.widget.timePickerEnabled;
  }

  get withDateRange(): boolean {
    return this.widget.kind !== 'date';
  }

  get orderReference() {
    return this.bookingDetails?.orderDetails?.id;
  }

  get notAvailable() {
    const dropOffTimeExist: boolean = this.withTime && !!this.dropOffTime;
    const dropOffDateExist: boolean = this.withDateRange && !this.withTime && !!this.dropOffDate;
    return !this.price.available && (dropOffTimeExist || dropOffDateExist);
  }

  get isThisItemInCart() {
    return this.cartState?.items.some(item => item.itemId === this.articleId);
  }

  get pickUpHours() {
    return Object.keys(this.possibleHours);
  }

  get dropOffHours() {
    return this.possibleHours[this.pickUpTime] || [];
  }

  get buttonText() {
    const pickDate = trans('articleBooking.buttonPickDateText', this.locale, this.translations);
    const returnDate = trans('articleBooking.buttonReturnText', this.locale, this.translations);
    const pickTime = trans('articleBooking.buttonPickTimeText', this.locale, this.translations);
    const returnTime = trans('articleBooking.buttonReturnTimeText', this.locale, this.translations);
    const returnDatePickTime = trans('articleBooking.buttonPickTimeOrReturnDateText', this.locale, this.translations);
    const returnDateReturnTime = trans('articleBooking.buttonReturnDateOrReturnTimeText', this.locale, this.translations);
    const selectTimeSlotHint = trans('articleBooking.selectTimeSlotHint', this.locale, this.translations);

    if (this.price.available) {
      const price = priceWithMileagePerTime(this.price, this.priceOptions, this.amountToBook, this.locale, this.translations, this.price.mileageOptions, this.selectedMileageId);

      return trans('articleBooking.addToCart', this.locale, this.translations, { amount: price });
    } else {
      if (this.timePicker) {
        if (!this.pickUpDate) {
          return pickDate;
        } else {
          return selectTimeSlotHint;
        }
      }

      if (this.noDateNoTime) {
        return pickDate;
      } else if (this.noDateTime) {
        if (!this.pickUpDate) {
          return pickDate;
        } else if (!this.pickUpTime) {
          return pickTime;
        } else {
          return returnTime;
        }
      } else if (this.dateNoTime) {
        if (!this.pickUpDate) {
          return pickDate;
        } else {
          return returnDate;
        }
      } else if (this.dateTime) {
        if (!this.pickUpDate) {
          return pickDate;
        } else {
          if (!this.dropOffDate) {
            if (!this.pickUpTime) {
              return returnDatePickTime;
            } else {
              if (!this.suspectedDropOffDate) {
                return returnDate;
              } else {
                return returnDateReturnTime;
              }
            }
          } else {
            if (!this.pickUpTime) {
              return pickTime;
            } else {
              return returnTime;
            }
          }
        }
      }
    }
  }

  render() {
    return (
      <Host class="CalendarTemplateContainerDimensions">
        <rentware-item-template-range
          itemId={this.articleId}
          itemDetails={this.itemDetails}
          isThisItemInCart={this.isThisItemInCart}
          selectedLocationId={this.selectedLocationId}
          selectedVariationId={this.selectedVariationId}
          selectedMileageId={this.selectedMileageId}
          orderReference={this.orderReference}
          locale={this.locale}
          translations={this.translations}
          withTime={this.withTime}
          withDateRange={this.withDateRange}
          onLocationChange={this.handleLocationChange}
          onVariationChange={this.handleVariationChange}
          onMileageChange={this.handleMileageChange}
          loading={this.isLoading}
          calendarLoading={this.isCalendarLoading}
          notAvailable={this.notAvailable}
          amountToAdd={this.amountToBook}
          onCheckoutClick={this.handleOpenCheckout}
          onSubmit={this.handleAddToCart}
          onChangeAmountToBook={this.handleChangeAmountToBook}
          price={this.price}
          loadingPickUpHours={this.loadingPickUpHours}
          pickUpDate={this.pickUpDate}
          pickUpTime={this.pickUpTime}
          pickUpHours={this.pickUpHours}
          onPickUpTimeChange={this.handlePickUpTimeChange}
          loadingDropOffHours={this.loadingDropOffHours}
          dropOffDate={this.dropOffDate}
          suspectedDropOffDate={this.suspectedDropOffDate}
          dropOffHours={this.dropOffHours}
          dropOffTime={this.dropOffTime}
          onDropOffTimeChange={this.handleDropOffTimeChange}
          timeSlotPicker={this.timeSlotPicker}
          daysAvailability={this.daysAvailability}
          firstAvailableDate={this.firstAvailableDate}
          lastAvailableDate={this.lastAvailableDate}
          onRangeDayClick={this.handleDayClick}
          onResetCalendar={this.handleResetCalendar}
          onRangeMonthClick={this.handleChangeMonth}
          buttonText={this.buttonText}
        />
      </Host>
    );
  }
}
